-- | Signature email modals.
-- |
-- | Allows users to edit signature email details and confirm Step 3 signature
-- | email requests. The app runtime sends the confirmed requests.
module Widgets.Signature.Email where

import Common

import API.Account (AccountInfo)
import API.RecentForms (RecentForm)
import API.SelStudent (studentName)
import API.Signature (SignatureRequest)
import API.Types (DocumentID)
import Concur.Core.Patterns (loopState)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (filter)
import Data.Array as Arr
import Data.FunctorWithIndex (mapWithIndex)
import Widgets.Checkbox (labelCheckbox)
import Widgets.Confirmation (confirmWidget)
import Widgets.Signature.Types (SignatureField)

type EmailAction_ a = EmailAction a a
data EmailAction a b
    = SaveEmail a
    | SaveAndSendEmail b


-- | Prompts the user to enter a single email address and requests the backend
-- | to send it if the user clicks send.
singleEmailWidget :: EmailModalState -> DocumentID -> SignatureField
    -> Widget HTML (EmailAction EmailModalState SignatureRequest)
singleEmailWidget initState docId field = runSingleEmailWidget initState field >>= case _ of
    SaveEmail e -> pure $ SaveEmail e
    SaveAndSendEmail state'@{email, comment} -> do
       conf <- confirmWidget (lookupTranslation "confirm-email-title")
                             (lookupTranslation "yes")
                             (lookupTranslation "confirm-email" <> " " <> email <> "?")
       if conf
           then pure $ SaveAndSendEmail { docId, email, comment, key: field.key }
           else singleEmailWidget state' docId field


type EmailModalState = { email :: String, comment :: String }

-- | Prompts the user to enter a single email address.
runSingleEmailWidget :: EmailModalState -> SignatureField
    -> Widget HTML (EmailAction_ EmailModalState)
runSingleEmailWidget state field = loopState state \state'@{email, comment} -> D.form
    [ P.className "signature-email-single-form modal-grid"
    , P.action "javascript:void(0);"
    , (Right $ SaveAndSendEmail state') <$ P.onSubmit
    ]
    [ D.h1
        [ P.className "modal-heading" ]
        [ D.text field.emailLabel ]
    , D.input
        [ Left <$> state' { email = _ } <$> P.unsafeTargetValue <$> P.onChange
        , P.value email, P._id "email-input"
        , P._type "email", P.required true
        , P.autoComplete "email"
        , P.placeholder $ lookupTranslation "email-address"
        , P.className "widget-input email-address"
        ]
    , D.div [ P.className "signature-comment-container" ]
        [ D.label
            [ P.htmlFor "comment-input"
            , P.className "signature-comment-label"
            ]
            [ D.text $ lookupTranslation "signature-comment" ]
        , D.textarea
            [ Left <$> state' { comment = _ } <$> P.unsafeTargetValue <$> P.onChange
            , P.value comment, P._id "comment-input"
            , P.placeholder $ lookupTranslation "signature-comment-placeholder"
            , P.className "widget-input signature-comment"
            ] []
        ]
    , D.div
        [ P.className "btn-group btn-group-email-single modal-buttons" ]
        [ D.button
            [ P.className "btn", P._type "submit" ]
            [ D.text $ lookupTranslation "send-email" ]
        ]
    ]


-- | State and result of the "email all" form. Contains a list of people to email.
type EmailList = Array EmailListEntry
type EmailListEntry =
    { field :: SignatureField
    , email :: String
    , selected :: Boolean
    -- ^ Whether that user is currently selected.
    , doc :: Maybe RecentForm
    -- ^ When the `emailAllForm` is used to select emails from multiple
    -- ^ different documents at once, this is used to display information about
    -- ^ which document this entry is associated with.
    }

emailAllForm :: AccountInfo -> DocumentID -> EmailList
    -> Widget HTML (Array SignatureRequest)
emailAllForm account docId emails' = runEmailAllForm account emails' >>= \emails -> do
       conf <- confirmWidget (lookupTranslation "confirm-email-title")
                             (lookupTranslation "yes")
                             (lookupTranslation "confirm-email-multiple")
       if conf
           then pure $ map toSigReq $ filter _.selected emails
           else emailAllForm account docId emails
  where
    toSigReq :: EmailListEntry -> SignatureRequest
    toSigReq { doc, email, field: { key } } =
        -- HACK: If the entry has a document set ignore the doc ID passed to this function
        { docId: maybe docId _.id doc, email, comment: "", key }

-- | Takes an `EmailList` and shows a form allowing the user to edit the emails
-- | and select which ones to include in the results, then returns an email
-- | list with unchecked entries excluded.
runEmailAllForm :: AccountInfo -> EmailList -> Widget HTML EmailList
runEmailAllForm account emails = loopState emails $ renderEmailAllForm account

renderEmailAllForm :: AccountInfo -> EmailList -> Widget HTML (Either EmailList EmailList)
renderEmailAllForm account emails = D.form
    [ P.className "signature-email-all-form modal-grid"
    , P.action "javascript:void(0);"
    , (Right $ emails) <$ P.onSubmit
    ]
    [ D.h1
        [ P.className "modal-heading" ]
        [ D.text $ lookupTranslation "request-signatures-for" ]
    , D.div
        [ P.className "email-all-rows" ]
        $ [selectAllCheck] <> mapWithIndex emailRow emails
    , actionBtns
    ]
  where
    emailRow idx ent = Left <$> 
        (\e -> fromMaybe emails $ Arr.updateAt idx e emails) <$>
        renderEmailRow account idx ent

    selectAll v = map (\e -> e { selected = v }) emails
    allSelected = all (_.selected) emails
    selectAllCheck = Left <$> selectAll <$> labelCheckbox "select-all-emails"
        (lookupTranslation "select-all") allSelected

    actionBtns = D.div
        [ P.className "btn-group btn-group-email-all modal-buttons" ]
        [ D.button
            [ P.className "btn"
            , P._type "submit"
            ]
            [ D.text $ lookupTranslation "send-email" ]
        ]

renderEmailRow :: AccountInfo -> Int -> EmailListEntry -> Widget HTML EmailListEntry
renderEmailRow account idx ent@{ field, email, selected } = D.div
    [ P.className "email-all-row" ]
    [ D.input
        [ P._type "checkbox"
        , P._id $ "checkbox" <> show idx
        , P.checked selected
        , ent { selected = not selected } <$ P.onChange
        , P.className "email-all-row-checkbox"
        ]
    , D.div
        [ P.className "email-wrapper" ]
        [ D.label
            [ P.htmlFor $ "checkbox" <> show idx ]
            [ D.text label ]
        , D.input $
            [ P._type "email", P.required selected
            , P._id $ "input" <> show idx
            , ent { email = _ } <$> P.unsafeTargetValue <$> P.onChange
            , P.value email
            , P.placeholder field.emailLabel
            , P.className "widget-input email-all-row-email"
            ] <> if selected then [] else [ P.disabled true ]
        ]
    ]
  where
    label = case ent.doc of
        Nothing -> field.emailLabel
        Just doc -> doc.form_type.abbr <> " - " <> sname doc <> " - " <> field.emailLabel <> sentLbl
    sname doc = studentName account doc.student
    wasSent = maybe false (\d -> any (\v -> v.data.key == field.key) d.verifications) ent.doc 
    sentLbl | wasSent = " (" <> lookupTranslation "already-emailed" <> ")"
    sentLbl = ""
